import { Grid, LinearProgress, Typography } from "@material-ui/core";
import { Cancel, Save } from "@material-ui/icons";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useGetMeQuery, useUpdateMeMutation } from "../../../api/rtk/MeApi";
import { RequiredCandidateMyProfile } from "../../../data/constants";
import Places from "../../../data/placeOfOrigin.json";
import { CandidateViewModel } from "../../../model/CandidateViewModel";
import { PersonType } from "../../../model/PersonType";
import { getValidDate } from "../../../utils/Dates";
import CustomButton from "../../common/buttons/CustomButton";
import RightButtonArea from "../../common/buttons/RightButtonArea";
import CustomDraggableDialog from "../../common/customDialog/CustomDraggableDialog";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { initialCandidate } from "../CandidateConstants";
import CandidateAddressFields from "./CandidateAddressFields";
import CandidateBusinessFields from "./CandidateBusinessFields";
import CandidateFields from "./CandidateFields";
import ExpertFields from "./ExpertFields";

const initialIsRequired: RequiredCandidateMyProfile = {
  isFirstnameEmpty: false,
  isLastnameEmpty: false,
  isGenderEmpty: false,
  isBirthDateEmpty: false,
  isStreetEmpty: false,
  isStreetNumberEmpty: false,
  isZipCodeEmpty: false,
  isCityEmpty: false,
  isCountryEmpty: false,
  isLanguageEmpty: false,
  isAhvNoEmpty: false,
  isNationalityEmpty: false,
  isPlaceOfOriginEmpty: false,
  isCantonEmpty: false,
  isEmployerInformationEmpty: false,
  isEmployerEmpty: false,
  isEmployerStreetEmpty: false,
  isEmployerStreetNumberEmpty: false,
  isEmployerZipCodeEmpty: false,
  isEmployerCityEmpty: false,
  isEmployerCountryEmpty: false,
  isEmailEmpty: false,
  isBankAccountEmpty: false,
};

const MyProfile: FC = () => {
  const history = useHistory();

  const { data: fetchedMe, isFetching: isMeFetching } = useGetMeQuery();
  const [updateMe, { isLoading: isUpdateMeLoading }] = useUpdateMeMutation();
  const isLoading = isMeFetching || isUpdateMeLoading;

  const [me, setMe] = useState(initialCandidate);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isAhvValid, setIsAhvValid] = useState(false);
  const [isCiceroValid, setIsCiceroValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(false);
  const [isRequired, setIsRequired] = useState(initialIsRequired);
  const [isEmailChangeDialogOpened, setIsEmailChangeDialogOpened] = useState(false);
  const [emailChangeNeedsConfirmation, setEmailChangeNeedsConfirmation] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(false);

  const isUserExpert = me?.type === PersonType.Expert;

  useEffect(() => {
    if (fetchedMe !== undefined) {
      setMe(fetchedMe);
    }
  }, [fetchedMe]);

  const handleCandidateChange = (partialCandidate: Partial<CandidateViewModel>) => {
    if (partialCandidate.emailAddress !== undefined) {
      // Email was changed, we will display a confirm dialog on save.
      setEmailChangeNeedsConfirmation(true);
    }
    setMe((prevCandidate) => ({ ...prevCandidate, ...partialCandidate }));
  };

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if (fetchedMe === undefined) {
    return <></>;
  }

  const close = () => history.goBack();

  const handleSave = async (checkEmailChangeConfirmation = true) => {
    if (checkEmailChangeConfirmation && emailChangeNeedsConfirmation) {
      setIsEmailChangeDialogOpened(true);
      return;
    }

    if (handleRequired() && handleValidation()) {
      if (!me.disablePersonalFields && !showConfirmationDialog) {
        setShowConfirmationDialog(true);
        return;
      }
      const response = await updateMe(me).unwrap();
      if (response.success) {
        close();
      }
    }
  };

  const handleEmpty = (field: any, isEmpty: string) => {
    if (field === "" || field === null) {
      setIsRequired((prevIsRequired) => ({ ...prevIsRequired, [isEmpty]: true }));
      return true;
    } else {
      setIsRequired((prevIsRequired) => ({ ...prevIsRequired, [isEmpty]: false }));
      return false;
    }
  };

  const handleValidDate = (value: any, isEmpty: string) => {
    value = getValidDate(value);

    return handleEmpty(value, isEmpty);
  };

  const handleRequired = () => {
    const resultList: boolean[] = [];
    resultList.push(handleEmpty(me.firstName, "isFirstnameEmpty"));
    resultList.push(handleEmpty(me.lastName, "isLastnameEmpty"));
    resultList.push(handleEmpty(me.gender, "isGenderEmpty"));
    resultList.push(handleValidDate(me.birthdate, "isBirthDateEmpty"));
    resultList.push(handleEmpty(me.street, "isStreetEmpty"));
    resultList.push(handleEmpty(me.streetNumber, "isStreetNumberEmpty"));
    resultList.push(handleEmpty(me.zipCode, "isZipCodeEmpty"));
    resultList.push(handleEmpty(me.city, "isCityEmpty"));
    resultList.push(handleEmpty(me.country, "isCountryEmpty"));
    resultList.push(handleEmpty(me.correspondenceLanguage, "isLanguageEmpty"));
    resultList.push(handleEmpty(me.ahvNumber, "isAhvNoEmpty"));
    resultList.push(handleEmpty(me.nationality, "isNationalityEmpty"));
    resultList.push(handleEmpty(me.employerInformation, "isEmployerInformationEmpty"));
    if (me.nationalityIsSwiss) {
      resultList.push(handleEmpty(me.placeOfOrigin, "isPlaceOfOriginEmpty"));
      resultList.push(handleEmpty(me.canton, "isCantonEmpty"));
    }
    if (!me.privateIsPreferredContact) {
      resultList.push(handleEmpty(me.employer, "isEmployerEmpty"));
      resultList.push(handleEmpty(me.employerCity, "isEmployerCityEmpty"));
      resultList.push(handleEmpty(me.employerCountry, "isEmployerCountryEmpty"));
      resultList.push(handleEmpty(me.employerStreet, "isEmployerStreetEmpty"));
      resultList.push(handleEmpty(me.employerStreetNumber, "isEmployerStreetNumberEmpty"));
      resultList.push(handleEmpty(me.employerZipCode, "isEmployerZipCodeEmpty"));
    }
    if (isUserExpert) {
      resultList.push(handleEmpty(me.bankAccount, "isBankAccountEmpty"));
    }

    return resultList.every((fieldStatus) => fieldStatus === false);
  };

  const handleValidation = () => isEmailValid && isAhvValid && isCiceroValid && isPhoneValid && isMobileValid;

  const handleConfirmEmailChange = () => {
    setIsEmailChangeDialogOpened(false);
    setEmailChangeNeedsConfirmation(false);
    handleSave(false);
  };

  const handleConfirmationDialog = () => {
    setMe((prevCandidate) => ({ ...prevCandidate, disablePersonalFields: true }));
    setShowConfirmationDialog(false);
    handleSave();
  };

  return (
    <>
      <Typography variant="h5">
        <TranslatedMessage id="home.me.edit" />
      </Typography>
      <RightButtonArea>
        <CustomButton tooltip={<TranslatedMessage id="common.save" />} onClick={handleSave}>
          <Save />
        </CustomButton>
        <CustomButton tooltip={<TranslatedMessage id="common.cancel" />} onClick={close}>
          <Cancel />
        </CustomButton>
      </RightButtonArea>
      <Grid container={true} spacing={5} direction="column">
        <Grid item={true}>
          <CandidateFields
            onPropertyValueChanges={handleCandidateChange}
            onChangeEmailIsValid={(isValid: boolean) => setIsEmailValid(isValid)}
            onChangeAhvIsValid={(isValid: boolean) => setIsAhvValid(isValid)}
            onChangeCiceroIsValid={(isValid: boolean) => setIsCiceroValid(isValid)}
            onChangePhoneIsValid={(isValid: boolean) => setIsPhoneValid(isValid)}
            onChangeMobileIsValid={(isValid: boolean) => setIsMobileValid(isValid)}
            isRequired={isRequired}
            candidate={me}
            places={Places}
          />
        </Grid>
        <Grid item={true}>
          <CandidateAddressFields
            onPropertyValueChanges={handleCandidateChange}
            isRequired={isRequired}
            candidate={me}
          />
        </Grid>
        {!me.privateIsPreferredContact && (
          <Grid item={true}>
            <CandidateBusinessFields
              onPropertyValueChanges={handleCandidateChange}
              isRequired={isRequired}
              candidate={me}
            />
          </Grid>
        )}
        {isUserExpert && (
          <Grid item={true}>
            <ExpertFields onPropertyValueChanges={handleCandidateChange} isRequired={isRequired} expert={me} />
          </Grid>
        )}
      </Grid>
      <CustomDraggableDialog
        title=""
        acceptText={<TranslatedMessage id="common.yes" />}
        cancelText={<TranslatedMessage id="common.no" />}
        content={<TranslatedMessage id="person.validation.confirmEmailChange" />}
        isOpen={isEmailChangeDialogOpened}
        onAccept={handleConfirmEmailChange}
        onCancel={() => setIsEmailChangeDialogOpened(false)}
      />
      <CustomDraggableDialog
        acceptText={<TranslatedMessage id="common.accept" />}
        cancelText={<TranslatedMessage id="common.cancel" />}
        content={<TranslatedMessage id="candidate.disableConfirmationDialog.message" />}
        isOpen={showConfirmationDialog}
        onAccept={handleConfirmationDialog}
        onCancel={() => setShowConfirmationDialog(false)}
        title={<TranslatedMessage id="common.attention" />}
      />
    </>
  );
};

export default MyProfile;
